import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Changelog API endpoint  
const CHANGELOG_API_BASE = 'https://api.prosecaptain.com';

const changelogAPI = axios.create({
  baseURL: CHANGELOG_API_BASE,
  headers: {
    'Content-Type': 'application/json'
  }
});

const ChangelogContext = createContext();

export const useChangelog = () => {
  const context = useContext(ChangelogContext);
  if (!context) {
    throw new Error('useChangelog must be used within a ChangelogProvider');
  }
  return context;
};

export const ChangelogProvider = ({ children }) => {
  const [hasUnseenChangelog, setHasUnseenChangelog] = useState(false);
  const [latestChangelog, setLatestChangelog] = useState(null);
  const [allChangelogs, setAllChangelogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('directus_token');

  const loadAllChangelogs = useCallback(async () => {
    if (!token) {
      process.env.NODE_ENV === "development" && console.log('🚨 No token found, skipping changelog load');
      return;
    }
    
    process.env.NODE_ENV === "development" && console.log('🔄 Loading changelogs...');
    setLoading(true);
    try {
      const currentDate = new Date().toISOString();
      const response = await changelogAPI.get('/items/changelog', {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          sort: '-date_created',
          fields: 'id,changelog_excerpt,changelog_link,changelog_category,changelog_end_date,date_created,changelog_thumbnail,status',
          filter: {
            status: { _eq: 'published' },
            _or: [
              { changelog_end_date: { _null: true } },
              { changelog_end_date: { _gte: currentDate } }
            ]
          }
        }
      });

      process.env.NODE_ENV === "development" && console.log('📦 Changelogs response:', response.data);

      if (response.data.data && response.data.data.length > 0) {
        const changelogs = response.data.data;
        setAllChangelogs(changelogs);
        setLatestChangelog(changelogs[0]); // First is latest due to sort
        process.env.NODE_ENV === "development" && console.log('✅ Loaded', changelogs.length, 'changelogs');

        // Check if user has seen latest changelog
        const lastSeenChangelogId = localStorage.getItem('last_seen_changelog_id');
        
        if (!lastSeenChangelogId || parseInt(lastSeenChangelogId) < changelogs[0].id) {
          setHasUnseenChangelog(true);
        }
      } else {
        process.env.NODE_ENV === "development" && console.log('📭 No changelogs found');
        setAllChangelogs([]);
        setLatestChangelog(null);
      }
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('❌ Error loading changelogs:', error);
      process.env.NODE_ENV === "development" && console.error('Full error details:', error.response?.status, error.response?.statusText);
      // Set empty arrays on error
      setAllChangelogs([]);
      setLatestChangelog(null);
      setHasUnseenChangelog(false);
    } finally {
      setLoading(false);
      process.env.NODE_ENV === "development" && console.log('🏁 Changelog loading finished');
    }
  }, [token]);

  useEffect(() => {
    loadAllChangelogs();
  }, [loadAllChangelogs]);

  const markChangelogAsSeen = (changelogId) => {
    localStorage.setItem('last_seen_changelog_id', changelogId.toString());
    setHasUnseenChangelog(false);
  };

  const markChangelogModalAsSeen = (changelogId) => {
    const modalSeenIds = JSON.parse(localStorage.getItem('modal_seen_changelog_ids') || '[]');
    if (!modalSeenIds.includes(changelogId)) {
      modalSeenIds.push(changelogId);
      localStorage.setItem('modal_seen_changelog_ids', JSON.stringify(modalSeenIds));
    }
    // Also mark as seen in notifications
    markChangelogAsSeen(changelogId);
  };

  const shouldShowModal = (changelogId) => {
    const modalSeenIds = JSON.parse(localStorage.getItem('modal_seen_changelog_ids') || '[]');
    return !modalSeenIds.includes(changelogId);
  };


  const value = {
    hasUnseenChangelog,
    latestChangelog,
    allChangelogs,
    loading,
    loadAllChangelogs,
    markChangelogAsSeen,
    markChangelogModalAsSeen,
    shouldShowModal
  };

  return (
    <ChangelogContext.Provider value={value}>
      {children}
    </ChangelogContext.Provider>
  );
};

export default ChangelogProvider;
